import dayjs from "dayjs"
import { useMemo } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

import { faIcon } from "@utils/faIcon"
import formatDate from "@utils/formatDate"

import { AccessCheck } from "@particles/index"

import { Button, Icon, IconButton, Tooltip } from "@atoms"

import { DatePicker } from "@molecules"

import TimelineViewMenu from "@pages/Jobs/JobList/views/JobTimelineView/TimelineViewMenu/TimelineViewMenu"
import UnscheduledJobsHeader from "@pages/Jobs/JobList/views/JobTimelineView/UnscheduledJobsHeader/UnscheduledJobsHeader"
import useJobTimelineViewData from "@pages/Jobs/JobList/views/JobTimelineView/hooks/useJobTimelineViewData"
import useJobTimelineViewEventHandlers from "@pages/Jobs/JobList/views/JobTimelineView/hooks/useJobTimelineViewEventHandlers"
import useJobTimelineViewNavigation from "@pages/Jobs/JobList/views/JobTimelineView/hooks/useJobTimelineViewNavigation"
import useJobTimelineViewStates from "@pages/Jobs/JobList/views/JobTimelineView/hooks/useJobTimelineViewStates"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./JobTimelineViewControls.module.scss"
import JobTimelineViewZoom from "./JobTimelineViewZoom/JobTimelineViewZoom"

export default function JobTimelineViewControls() {
    const isDesktop = useIsDesktop()

    const {
        visibleDateRange,
        unscheduledJobsColumnStatus,
        expandUnscheduledJobsColumn,
        timelineViewOrientation,
        timeFrameType,
    } = useJobTimelineViewStates()

    const nextDate = new Date(visibleDateRange.start)
    const previousDate = new Date(visibleDateRange.start)
    nextDate.setDate(visibleDateRange.start.getDate() + 1)
    previousDate.setDate(visibleDateRange.start.getDate() - 1)

    const { onNavTopBarPreviousButtonClick, onNavTopBarNextButtonClick, onNavTopBarNowButtonClick } =
        useJobTimelineViewEventHandlers()

    const { scrollToStartOfWorkingDay } = useJobTimelineViewNavigation()

    const { isLoading, unscheduledJobsCount } = useJobTimelineViewData()

    const { localDateString: currentDateString } = formatDate({
        datetime: visibleDateRange.start,
        preferredTimezone: undefined,
        short: true,
    })

    const { localDateString: currentEndDateString } = formatDate({
        datetime: visibleDateRange.end,
        preferredTimezone: undefined,
        short: true,
    })

    const { localDateString: previousDateString } = formatDate({
        datetime: previousDate,
        preferredTimezone: undefined,
        short: true,
    })

    const { localDateString: nextDateString } = formatDate({
        datetime: nextDate,
        preferredTimezone: undefined,
        short: true,
    })

    const datePickerButtonContent = useMemo(() => {
        if (timeFrameType === "day") {
            return currentDateString
        } else if (timeFrameType === "threeDays" || timeFrameType === "week") {
            return `${currentDateString} - ${currentEndDateString}`
        } else if (timeFrameType === "month") {
            return dayjs(visibleDateRange.start).format("MMMM YYYY")
        }
    }, [timeFrameType, currentDateString, currentEndDateString])

    const previousDateButtonLabel = useMemo(() => {
        if (timeFrameType === "day") {
            return previousDateString
        } else if (timeFrameType === "threeDays") {
            return "Previous 3 days"
        } else if (timeFrameType === "week") {
            return "Previous week"
        } else if (timeFrameType === "month") {
            return "Previous month"
        }
    }, [previousDateString])

    const nextDateButtonLabel = useMemo(() => {
        if (timeFrameType === "day") {
            return nextDateString
        } else if (timeFrameType === "threeDays") {
            return "Next 3 days"
        } else if (timeFrameType === "week") {
            return "Next week"
        } else if (timeFrameType === "month") {
            return "Next month"
        }
    }, [nextDateString])

    return (
        <div className={styles.base}>
            <div className={styles.controls}>
                <div className={styles.navControls}>
                    <DatePicker
                        onSelect={(date) => {
                            if (date) {
                                scrollToStartOfWorkingDay(date)
                            }
                        }}
                        defaultSelected={visibleDateRange.start}
                    >
                        <Button
                            onClick={() => null}
                            size="md"
                            variant="subtle"
                            leftIcon={faIcon({ name: "calendar", style: "regular" })}
                            className={styles.datePicker}
                        >
                            <span className={styles.stringDate}>{datePickerButtonContent}</span>
                        </Button>
                    </DatePicker>
                    <div className={styles.previousAndNextDays}>
                        <Tooltip body={previousDateButtonLabel}>
                            <IconButton
                                icon={faIcon({ name: "angle-left", style: "regular" })}
                                onClick={onNavTopBarPreviousButtonClick}
                                size="lg"
                                variant="ghost"
                                iconRotation={timelineViewOrientation === "vertical" ? 90 : undefined}
                            />
                        </Tooltip>
                        <Tooltip body={nextDateButtonLabel}>
                            <IconButton
                                icon={faIcon({ name: "angle-right", style: "regular" })}
                                onClick={onNavTopBarNextButtonClick}
                                size="lg"
                                variant="ghost"
                                iconRotation={timelineViewOrientation === "vertical" ? 90 : undefined}
                            />
                        </Tooltip>
                    </div>

                    <Button size="md" variant="subtle" onClick={onNavTopBarNowButtonClick}>
                        Now
                    </Button>
                    {isLoading && (
                        <div className={styles.spinner}>
                            <Icon icon={faIcon({ name: "spinner-third", style: "regular" })} spin={true} />
                        </div>
                    )}
                </div>

                <div className={styles.viewControls}>
                    <JobTimelineViewZoom />
                    <TimelineViewMenu />
                    <AccessCheck
                        permissions={{
                            jobs_edit_permission: PERMISSION_LEVEL.FULL,
                        }}
                    >
                        {unscheduledJobsColumnStatus === "expanded" || !isDesktop ? null : (
                            <Button
                                size="md"
                                variant="subtle"
                                leftIcon={
                                    unscheduledJobsCount > 0
                                        ? faIcon({ name: "triangle-exclamation", style: "solid" })
                                        : undefined
                                }
                                leftIconClassName={styles.unscheduledJobsIcon}
                                onClick={expandUnscheduledJobsColumn}
                                noDebounce={true}
                            >
                                {unscheduledJobsCount} Unscheduled Jobs
                            </Button>
                        )}
                    </AccessCheck>
                </div>
            </div>
            <UnscheduledJobsHeader />
        </div>
    )
}
